import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle, Clock, MessageCircle, Phone, ArrowRight } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { SERVICE_PAGES } from "@/data/mock";
import { useStorePicker } from "@/hooks/useStorePicker";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const svc = SERVICE_PAGES[slug];
  const { open } = useStorePicker();
  if (!svc) return <Navigate to="/" replace />;

  return (
    <PageLayout>
      {/* Hero */}
      <section className="pt-28 lg:pt-32 pb-16 bg-gradient-to-br from-sky-50/60 via-white to-lime-50/40 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-3 h-3 bg-sky-300/60 rounded-full animate-float pointer-events-none" />
        <div className="absolute bottom-20 left-16 w-2.5 h-2.5 bg-lime-300/60 rounded-full animate-float pointer-events-none" style={{animationDelay: "1s"}} />

        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Link to="/#services" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-sky-600 mb-4">
                ← All services
              </Link>
              <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-3">Blitzwash Service</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mb-4 leading-[1.05]">
                {svc.title}
              </h1>
              <p className="text-lg text-slate-500 leading-relaxed mb-6">{svc.subtitle}</p>

              <div className="flex flex-wrap gap-3 mb-8">
                <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-sky-100 text-sky-700 text-sm font-bold" data-testid="service-price-badge">
                  {svc.price}
                </div>
                <div className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-lime-100 text-lime-700 text-sm font-semibold">
                  <Clock className="w-4 h-4" /> {svc.turnaround}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => open("whatsapp")}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-lime-400 hover:bg-lime-300 text-slate-900 font-bold text-sm transition-colors shadow-lg shadow-lime-500/20"
                  data-testid="service-book-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" /> Book on WhatsApp
                </button>
                <button
                  type="button"
                  onClick={() => open("call")}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-white border border-slate-300 hover:border-sky-400 text-slate-800 font-bold text-sm transition-colors"
                  data-testid="service-call-btn"
                >
                  <Phone className="w-4 h-4" /> Call Us
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl shadow-sky-900/10">
                <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After showcase (shoe-care specific) */}
      {slug === "shoe-care" && (
        <section className="py-14 bg-gradient-to-br from-slate-900 via-sky-900 to-slate-900 text-white">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <span className="inline-block text-sm font-semibold text-lime-300 tracking-wider uppercase mb-3">
              Real Blitzwash Result
            </span>
            <h2 className="text-3xl lg:text-4xl font-extrabold mb-3 tracking-tight">
              Dirty shoes? Not your style.
            </h2>
            <p className="text-sky-100 mb-8 max-w-xl mx-auto">
              This is an actual customer sneaker restoration at our Jaipur store. From caked-in mud to brand-new white — with free pickup and delivery.
            </p>
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-sky-500/20 border border-white/10">
              <img src="/images/shoe-before-after.jpg" alt="Blitzwash shoe cleaning before and after result" className="w-full" />
            </div>
          </div>
        </section>
      )}

      {/* Highlights + Ideal For */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl lg:text-3xl font-extrabold text-slate-900 tracking-tight mb-5">What's included</h2>
            <ul className="space-y-3">
              {svc.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-lime-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl lg:text-3xl font-extrabold text-slate-900 tracking-tight mb-5">Ideal for</h2>
            <div className="flex flex-wrap gap-2">
              {svc.idealFor.map((it, i) => (
                <span key={i} className="inline-flex px-3.5 py-2 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-sm font-medium">
                  {it}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-slate-50/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-3">Our Process</span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">How we care for your {svc.title.toLowerCase()}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {svc.process.map((p, i) => (
              <div key={i} className="relative bg-white rounded-3xl border border-slate-200 p-5">
                <div className="absolute -top-3 -left-1 text-6xl font-black text-slate-100 select-none leading-none">0{i+1}</div>
                <div className="relative">
                  <h3 className="text-base font-bold text-slate-900 mb-2">{p.step}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl lg:text-3xl font-extrabold text-slate-900 tracking-tight">Explore other services</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(SERVICE_PAGES).filter(([s]) => s !== slug).map(([s, p]) => (
              <Link key={s} to={`/services/${s}`} className="group p-4 rounded-2xl border border-slate-200 hover:border-sky-300 hover:bg-sky-50/40 transition-colors">
                <p className="text-sm font-bold text-slate-900 group-hover:text-sky-700">{p.title.replace(" in Jaipur", "")}</p>
                <p className="text-xs text-slate-500 mt-1">from {p.price}</p>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-sky-600 mt-3" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
